import {memo, useState} from 'react'
import {Bot, User, ChevronDown, ChevronRight, Info} from 'lucide-react'
import {cn, makeUrlsClickable, formatElapsedTime} from '../../utils'
import type {Message} from '../../types'
import {ToolUseBlockComponent} from './ToolUseBlock'

interface ChatMessageProps {
    message: Message
    isStreaming?: boolean
}

export const ChatMessage = memo(function ChatMessage({
                                                         message,
                                                         isStreaming = false,
                                                     }: ChatMessageProps) {
    const isUser = message.role === 'user'
    const contentWithLinks = makeUrlsClickable(message.content)
    const [showMetadata, setShowMetadata] = useState(false)

    // Debug logging for assistant messages
    if (!isUser && message.contentBlocks) {
        console.log('[ChatMessage] Rendering assistant message with', message.contentBlocks.length, 'content blocks')
        const toolBlocks = message.contentBlocks.filter(b => b.type === 'tool')
        if (toolBlocks.length > 0) {
            console.log('[ChatMessage] 🔧 Rendering', toolBlocks.length, 'tool blocks:',
                toolBlocks.map(b => b.type === 'tool' ? b.toolBlock.name : ''))
        }
    }


    // Separate text and tool blocks for assistant messages
    type TextBlock = { type: 'text'; content: string };
    type ToolBlock = { type: 'tool'; toolBlock: any };

    function isTextBlock(block: any): block is TextBlock {
        return block.type === 'text';
    }

    function isToolBlock(block: any): block is ToolBlock {
        return block.type === 'tool';
    }

    const [showToolUsage, setShowToolUsage] = useState(false);
    let toolBlocks: ToolBlock[] = [];
    // Collect tool blocks for the expandable section
    if (!isUser && message.contentBlocks && message.contentBlocks.length > 0) {
        toolBlocks = message.contentBlocks.filter(isToolBlock);
    }

    return (
        <div className={cn(`message-row ${message.role}`, isUser ? "flex-row-reverse space-x-reverse" : "")}>
            <div className="message-avatar">
                {isUser ? (<User size={16}/>) : (<Bot size={16}/>)}
            </div>
            <div className="message-bubble">
                {/* Render contentBlocks in order, but replace tool blocks with Tool Usage button */}
                {!isUser && message.contentBlocks && message.contentBlocks.length > 0 ? (
                    <>
                        {message.contentBlocks.map((block, index) => {
                            if (isTextBlock(block)) {
                                const textWithLinks = makeUrlsClickable(block.content);
                                const isLastBlock = index === message.contentBlocks!.length - 1;
                                return (
                                    <div
                                        key={`text-${index}`}
                                        className={cn(
                                            "whitespace-pre-wrap break-words text-sm leading-relaxed",
                                            index > 0 && "mt-3"
                                        )}
                                    >
                                        <span dangerouslySetInnerHTML={{__html: textWithLinks}}/>
                                        {isStreaming && isLastBlock && (
                                            <span className="inline-block ml-1 text-sm animate-cursor-blink">...</span>
                                        )}
                                    </div>
                                );
                            } else if (isToolBlock(block)) {
                                return (
                                    <div key={`tool-usage-btn-${index}`} className="mt-3">
                                        <button
                                            onClick={() => setShowToolUsage(!showToolUsage)}
                                            className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-400 transition-colors"
                                        >
                                            {showToolUsage ? (
                                                <ChevronDown className="w-3 h-3"/>
                                            ) : (
                                                <ChevronRight className="w-3 h-3"/>
                                            )}
                                            <Info className="w-3 h-3"/>
                                            <span className="font-medium">Tool Usage</span>
                                        </button>
                                        {showToolUsage && (
                                            <div>
                                                {toolBlocks.map((block) => (
                                                    <div key={`tool-${block.toolBlock.toolUseId}`}
                                                         className="mb-3 last:mb-0">
                                                        <ToolUseBlockComponent toolBlock={block.toolBlock}/>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            }
                            return null;
                        })}
                    </>
                ) : (
                    <>
                        <div
                            className={cn(
                                "whitespace-pre-wrap break-words text-sm leading-relaxed",
                                isStreaming && "relative"
                            )}
                            dangerouslySetInnerHTML={{__html: contentWithLinks}}
                        />
                        {isStreaming && (
                            <span className="inline-block ml-1 text-sm animate-cursor-blink">Thinking...</span>
                        )}
                    </>
                )}

                {/* Metadata section */}
                {!isUser && message.metadata && !isStreaming && (
                    <div className="mt-3">
                        <button
                            onClick={() => setShowMetadata(!showMetadata)}
                            className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-400 transition-colors"
                        >
                            {showMetadata ? (
                                <ChevronDown className="w-3 h-3"/>
                            ) : (
                                <ChevronRight className="w-3 h-3"/>
                            )}
                            <Info className="w-3 h-3"/>
                            <span>Metadata</span>
                        </button>

                        {showMetadata && (
                            <div className="mt-2 p-3 bg-[#1a1d24] rounded-lg border border-[#3a3f4b] text-xs">
                                {message.metadata.usage && (
                                    <div className="mb-2">
                                        <div className="font-medium text-gray-400 mb-1">Token Usage</div>
                                        <div className="text-gray-500 space-y-1">
                                            <div>Input: {message.metadata.usage.inputTokens}</div>
                                            <div>Output: {message.metadata.usage.outputTokens}</div>
                                            <div>Total: {message.metadata.usage.totalTokens}</div>
                                        </div>
                                    </div>
                                )}

                                {message.metadata.metrics && (
                                    <div className="mb-2">
                                        <div className="font-medium text-gray-400 mb-1">Performance</div>
                                        <div className="text-gray-500">
                                            {message.metadata.metrics.totalLatencyMs !== undefined && (
                                                <div>Total
                                                    Latency: {(message.metadata.metrics.totalLatencyMs / 1000).toFixed(2)}s</div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {message.metadata.toolMetrics && Object.keys(message.metadata.toolMetrics).length > 0 && (
                                    <div className="mb-2">
                                        <div className="font-medium text-gray-400 mb-1">Tool Metrics</div>
                                        {Object.entries(message.metadata.toolMetrics).map(([toolName, metrics]) => (
                                            <div key={toolName} className="text-gray-500 mb-1">
                                                <div className="font-medium">{toolName}</div>
                                                <div className="ml-2 space-y-0.5">
                                                    <div>Invocations: {metrics.invocations}</div>
                                                    <div>Avg Duration: {metrics.average_duration_seconds.toFixed(3)}s
                                                    </div>
                                                    <div>Total Duration: {metrics.total_duration_seconds.toFixed(3)}s
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {message.metadata.cycleDurations && message.metadata.cycleDurations.length > 0 && (
                                    <div>
                                        <div className="font-medium text-gray-400 mb-1">Event Loop Cycles</div>
                                        <div className="text-gray-500">
                                            Cycles: {message.metadata.cycleDurations.length}
                                            <div className="ml-2">
                                                {message.metadata.cycleDurations.map((duration, idx) => (
                                                    <div key={idx}>Cycle {idx + 1}: {duration.toFixed(3)}s</div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {message.metadata.stopReason && (
                                    <div className="mt-2 pt-2 border-t border-[#3a3f4b]">
                                        <div className="font-medium text-gray-400">Stop Reason</div>
                                        <div className="text-gray-500">{message.metadata.stopReason}</div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Elapsed time for simple responses */}
                {!isUser && message.elapsed !== undefined && !isStreaming && !message.metadata && (
                    <div className="mt-2 text-xs text-gray-500">
                        ⏱️ Response time: {formatElapsedTime(message.elapsed)}
                    </div>
                )}
            </div>
        </div>
    )
})